import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { CreateSubscriptionOutput, GetPlanOutput } from "@smash-sdk/billing/01-2022";
import { Iam } from "@smash-sdk/iam/10-2019";
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { DisplayUser } from './models/DisplayUser.interface';
import { LoginUser } from './models/LoginUser.interface';
import { SignupUser } from './models/SignupUser.interface';
import authService from './services/auth.service';
import permissionService from './services/permission.service';

const storedUser: string | null = localStorage.getItem('identity');
const identity: DisplayUser | null = !!storedUser ? JSON.parse(storedUser) : null;


export interface AuthState extends AsyncState {
    signup: SignupState;
    identity: DisplayUser | null;
    isAuthenticated: boolean;
    permissionsUpdate: PermissionsState;
}

export interface PermissionsState extends AsyncState { }

export interface SignupState extends AsyncState {
    plan: GetPlanOutput['plan'] | null;
    subscription: CreateSubscriptionOutput['subscription'] | null;
}

export const initialState: AuthState = {
    signup: {
        plan: null,
        subscription: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    identity: identity,
    isAuthenticated: authService.hasActiveSession(),
    permissionsUpdate: {
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: undefined,
}

export const getAnonymousToken = createAsyncThunk(
    'auth/getAnonymousToken',
    async (_, thunkApi) => {
        try {
            return await authService.createAnonymousUser();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const signup = createAsyncThunk(
    'auth/signup',
    async (user: SignupUser, thunkApi) => {
        try {
            const { identity, subscription, plan } = await authService.signup(user);
            return { identity, subscription, plan };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const login = createAsyncThunk(
    'auth/login',
    async (user: LoginUser, thunkApi) => {
        try {
            return await authService.login(user);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const logout = createAsyncThunk(
    'auth/logout',
    async () => {
        await authService.logout();
    }
)

export const verifyJwt = createAsyncThunk(
    'auth/verify-jwt',
    async (_, thunkApi) => {
        try {
            return await authService.verifyJwt();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const updatePermissions = createAsyncThunk(
    'auth/updatePermissions',
    async (_, thunkApi) => {
        try {
            return await permissionService.updatePermissions();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const updateIdentity = createAsyncThunk(
    'auth/updateIdentity',
    async (_, thunkApi) => {
        try {
            const iam = new Iam();
            const { account } = await iam.getAccount();
            const { auth }: any = thunkApi.getState();
            return {
                identity:
                {
                    ...auth.identity,
                    firstName: account.firstName,
                    lastName: account.lastName,
                    username: account.username
                }
            };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            // UPDATE IDENTITY
            // .addCase(updateIdentity.pending, (state) => { // this cause a full reload of the page
            //     state.isLoading = true;
            // })
            .addCase(updateIdentity.fulfilled, (state, action) => ({
                ...state,
                isLoading: false,
                isSuccess: true,
                identity: action.payload.identity
            }))
            .addCase(updateIdentity.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
            })
            // SIGNUP
            .addCase(signup.pending, (state) => {
                state.signup.isLoading = true;
            })
            .addCase(signup.fulfilled, (state, action) => {
                state.signup.isLoading = false;
                state.signup.isSuccess = true;
                state.signup.plan = action.payload.plan;
                state.signup.subscription = action.payload.subscription;
                state.isAuthenticated = true;
                state.identity = action.payload.identity;
            })
            .addCase(signup.rejected, (state, action) => {
                state.signup.isLoading = false;
                state.signup.isError = true;
                state.signup.error = action.payload as SerializedError;
                state.identity = null;
                state.isAuthenticated = false;

            })
            // Anonymous
            .addCase(getAnonymousToken.pending, (state) => {
                state.isSuccess = false;
                state.isError = false;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(getAnonymousToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isAuthenticated = false;
                state.identity = action.payload.identity;
            })
            .addCase(getAnonymousToken.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
                state.isAuthenticated = false;
                state.error = action.error;
            })
            // LOGIN
            .addCase(login.pending, (state) => {
                state.isSuccess = false;
                state.isError = false;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.identity = action.payload.identity;
                state.isAuthenticated = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.identity = null;
                state.isAuthenticated = false;
                state.error = action.payload as SerializedError;
            })
            // UPDATE PERMISSIONS
            .addCase(updatePermissions.pending, (state) => {
                state.permissionsUpdate.isSuccess = false;
                state.permissionsUpdate.isError = false;
                state.permissionsUpdate.isLoading = true;
            })
            .addCase(updatePermissions.fulfilled, (state, action) => {
                state.permissionsUpdate.isSuccess = true;
                state.permissionsUpdate.isError = false;
                state.permissionsUpdate.isLoading = false;
            })
            .addCase(updatePermissions.rejected, (state, action) => {
                state.permissionsUpdate.isSuccess = false;
                state.permissionsUpdate.isError = true;
                state.permissionsUpdate.isLoading = false;
                state.error = action.payload as SerializedError;
            })
            // LOGOUT
            .addCase(logout.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(logout.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.identity = null;
                state.isAuthenticated = false;
            })
            // VERIFY
            .addCase(verifyJwt.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(verifyJwt.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isAuthenticated = action.payload;
            })
            .addCase(verifyJwt.rejected, (state) => {
                state.isLoading = false;
                state.isError = true;
                state.isAuthenticated = false;
            })
    }
});

export const { reset } = authSlice.actions;

export const selectedUser = (state: RootState) => state.auth;

export const isAuth = (state: RootState) => state.auth.isAuthenticated;
export const permissionsError = (state: RootState) => state.auth.permissionsUpdate.isError;
export const permissionsUpdated = (state: RootState) => state.auth.permissionsUpdate.isSuccess;
export const permissionsUpdating = (state: RootState) => state.auth.permissionsUpdate.isLoading;

export default authSlice.reducer;